import React from "react";
import Link from "next/link";

import getMarqueeCreatives from "@/api/requests/consumerApi/getMarqueeCreatives";
import { getDeals } from "@/api/requests/getDeals";
import { getPromotions } from "@/api/requests/getPromotions";
import { getUserLocationData } from "@/api/requests/getUserLocation";
import { CATEGORY_NAMES_FOR_SHOP } from "@/constants/dispensary";
import { Action, Category } from "@/constants/events";
import { AdMarqueePageLocation, Creative } from "@/custom-types/AdMarquee";
import { Deal } from "@/custom-types/Deals";
import { Promotion } from "@/custom-types/Promotions";
import useDomainCountryCode from "@/hooks/useDomainCountryCode";
import { useEventTracker } from "@/hooks/useEventTracker";
import { coinListDataFactory } from "@/utils/coinListDataFactory";
import { createGetServerSideProps } from "@/utils/createGetServerSideProps";
import { getCountryCodeHeader } from "@/utils/requestContext";

import AdMarqueeCarousel from "@/components/AdMarqueeCarousel";
import BogosCarousel from "@/components/BogosCarousel";
import Breadcrumb from "@/components/Breadcrumb";
import Breadcrumbs from "@/components/Breadcrumbs";
import BundleCarousel from "@/components/BundleCarousel";
import CartCarousel from "@/components/CartCarousel";
import CoinList from "@/components/CoinList";
import DealsDisclaimer from "@/components/DealsDisclaimer";
import DealsHero from "@/components/DealsHero";
import DealsResults from "@/components/DealsResults";
import MetaTags from "@/components/MetaTags";
import PhenotypeCarousel from "@/components/PhenotypeCarousel";
import PromoResults from "@/components/PromoResults";
import PromotedStrainCarousel from "@/components/StrainCarousel";

const DEALS_PER_PAGE = 6;

type DealsPageProps = {
  bogoDeals: Deal[];
  bundleDeals: Deal[];
  cartDeals: Deal[];
  creatives: Creative[];
  deals: { deals: Deal[]; totalCount: number; dealCategories: string[] };
  promotions: Promotion[];
};

const DealsPage: React.FC<DealsPageProps> = ({
  bogoDeals,
  bundleDeals,
  cartDeals,
  creatives,
  deals,
  promotions,
}) => {
  const countryCode = useDomainCountryCode();
  const { publishEvent } = useEventTracker();

  const isCanada = countryCode === "CA";

  const coinCategories = Object.keys(CATEGORY_NAMES_FOR_SHOP).filter(
    (category) => deals.dealCategories.includes(category),
  );

  const coinListData = coinListDataFactory.createCoinListDataForShopCategories(
    coinCategories,
    CATEGORY_NAMES_FOR_SHOP,
  );

  return (
    <>
      <MetaTags
        description="Looking for weed deals happening today? Find 420 deals, special sales, promo codes, coupons, and discounts from cannabis shops in your area on Leafly"
        title="Weed Deals, 420 Promos, & BOGO Deals Near Me | Leafly"
      />

      <div className="container mt-xl mb-xxl">
        <h1 className="heading--m mb-lg">Weed deals near you</h1>

        <div className="flex items-center relative aspect-[343/255] md:aspect-[992/278] mb-section">
          {creatives.some((c) => c.creativeType === "dispensary") ? (
            <AdMarqueeCarousel
              pageType={AdMarqueePageLocation.DEALS_PAGE}
              creatives={creatives}
            />
          ) : (
            <DealsHero />
          )}
        </div>

        {deals.totalCount > 0 && (
          <>
            <h2 className="heading--m mb-md">Shop deals your way</h2>
            <CoinList
              className="mb-section"
              coinData={coinListData}
              hrefLink={"/shop"}
              hrefLinkQueryParam="&filter[deal_active]=true"
              hrefProductCatQueryKey="filter[category]"
            />
          </>
        )}

        <CartCarousel cartDeals={cartDeals} />
        <BundleCarousel bundleDeals={bundleDeals} />
        <BogosCarousel bogos={bogoDeals} />

        {deals.totalCount > 0 && (
          <>
            <h2 className="heading--m mb-md">Deals on weed by strain type</h2>
            <PhenotypeCarousel className="mb-section" asLink linkToDeals />

            <PromotedStrainCarousel
              asLink
              className="mb-section"
              onClick={(strain) =>
                publishEvent({
                  action: Action.click,
                  category: Category.dealMenuStrains,
                  label: `strain name - ${strain.strainName}`,
                })
              }
              title="Deals on top weed strains near you"
            />
          </>
        )}

        <DealsResults
          className="mb-section"
          initialDeals={deals.deals}
          totalCount={deals.totalCount}
        />

        <PromoResults className="mb-section" promotions={promotions} />

        <Link
          className="underline mb-section block"
          href="/deals/locations"
          onClick={() =>
            publishEvent({
              action: Action.click,
              category: Category.dealsMenuSeeAllLocations,
            })
          }
        >
          See all locations
        </Link>

        <p className="mb-section">
          {isCanada
            ? "Cannabis retail stores in Canada are now able to offer exclusive dispensary deals and discounts using Leafly. Weed deals often come in different ways ranging from first-time visit deals to veteran discounts, plus birthday deals and early bird specials. If you’re looking to save cash on cannabis (and who isn’t?), let Leafly help you get the biggest bang for your buck with our updated list of the hottest cannabis shop deals, sales, promo codes, and more near you."
            : "Cannabis dispensaries are now able to offer exclusive deals and discounts using Leafly. Weed deals often come in ways ranging from first-time dispensary deals to veteran discounts, plus birthday deals and early bird specials. If you’re looking to save cash on cannabis, let Leafly help you get the biggest bang for your buck with our updated list of the hottest deals near you happening now!"}
        </p>

        <DealsDisclaimer />
      </div>
      <Breadcrumbs className="mt-xl">
        <Breadcrumb currentLocation href="/deals">
          Deals
        </Breadcrumb>
      </Breadcrumbs>
    </>
  );
};

export const getServerSideProps = createGetServerSideProps<DealsPageProps>(
  async (context) => {
    const countryCode = getCountryCodeHeader(context.req.headers);

    const { coordinates } = await getUserLocationData(countryCode, context);

    const formattedCoordinates = {
      lat: coordinates?.lat || coordinates?.latitude,
      lon: coordinates?.lon || coordinates?.longitude || coordinates?.lng,
    };

    const [
      { deals: bogoDeals },
      { promotions },
      deals,
      { deals: cartDeals },
      { deals: bundleDeals },
      creatives,
    ] = await Promise.all([
      getDeals(formattedCoordinates, countryCode, 0, 16, {
        "filter[kind]": "bogo",
      }), // bogoDeals
      getPromotions(formattedCoordinates, countryCode), // promotions
      getDeals(formattedCoordinates, countryCode, 0, DEALS_PER_PAGE), // deals
      getDeals(formattedCoordinates, countryCode, 0, 16, {
        "filter[kind]": "cart",
      }), // cartDeals
      getDeals(formattedCoordinates, countryCode, 0, 16, {
        "filter[kind]": "bundle",
      }), // bundleDeals
      getMarqueeCreatives(
        AdMarqueePageLocation.DEALS_PAGE,
        formattedCoordinates,
      ),
    ]);

    return {
      props: {
        bogoDeals,
        bundleDeals,
        cartDeals,
        creatives,
        deals,
        promotions,
      },
    };
  },
);

export default DealsPage;
